import { Injectable, Signal, inject } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { Observable, catchError, map, of, tap } from 'rxjs';
import { PermissionsService } from '@shared/services/permissions.service';
import { SystemPermission } from '@shared/models/enums/system-permission.enum';
import { AuthService } from '@modules/auth/services/auth.service';
import { RequestBoardAccessApiService } from './request-board-access-api.service';

/**
 * Stan uprawnień tablicy „Szukam noclegu” (panel właściciela).
 *
 * `canPropose` = użytkownik ma `request_board.propose` (rola org OWNER/MANAGER; CLEANER nie).
 * Backend zwraca scalone uprawnienia (per-user ∪ rola org) w odpowiedzi logowania, ale
 * sesja zapisana w localStorage przed tą zmianą może ich nie zawierać — dlatego `refresh()`
 * dociąga je z `GET /auth/permissions` na starcie aplikacji i przy wejściu na tablicę.
 * Zapytanie idzie w tle (bez globalnego toasta błędu), a sesje gościa (rola `guest` — bez
 * organizacji i bez tablicy) w ogóle go nie wysyłają.
 * Frontend tylko chowa UI („Zaproponuj” / „Wycofaj”); egzekwuje backend (403).
 */
@Injectable({
  providedIn: 'root',
})
export class RequestBoardAccessService {
  private readonly api = inject(RequestBoardAccessApiService);
  private readonly permissions = inject(PermissionsService);
  private readonly authService = inject(AuthService);

  /** Czy można składać / wycofywać propozycje (`request_board.propose`). */
  readonly canPropose: Signal<boolean> = toSignal(
    this.permissions.hasPermission$(SystemPermission.REQUEST_BOARD_PROPOSE),
    { requireSync: true },
  );

  /**
   * Odświeża uprawnienia z backendu i zapisuje je w `PermissionsService`.
   * Błąd (np. 401 / brak sieci) nie zmienia stanu — zwraca bieżącą wartość `canPropose`.
   * Gość: bez zapytania — zwraca bieżącą wartość.
   */
  refresh(): Observable<boolean> {
    if (this.isGuestSession()) {
      return of(this.canPropose());
    }
    return this.api.getPermissions().pipe(
      tap(response => this.permissions.setUserPermissions(response.permissions as SystemPermission[])),
      map(response => response.permissions.includes(SystemPermission.REQUEST_BOARD_PROPOSE)),
      catchError(() => of(this.canPropose())),
    );
  }

  /** Start aplikacji: odśwież uprawnienia tylko przy ważnej sesji nie-gościa (bez blokowania bootstrapu). */
  refreshOnStartup(): void {
    if (!this.authService.isAuthenticated() || this.isGuestSession()) {
      return;
    }
    this.refresh().subscribe();
  }

  /**
   * Sesja gościa. `isGuest$` ustawia się dopiero PO `isAuthenticated$` przy guest-login,
   * więc sprawdzamy też rolę bieżącego użytkownika (`role === 'guest'`).
   */
  private isGuestSession(): boolean {
    return this.authService.isGuest() || this.authService.getCurrentUser()?.isGuest === true;
  }
}
